import { useState } from "react";
import { Card } from "../shared";

export function PlanBillingSection({ tier, isOwner, isPaid, planLabel, planColor, planDesc, companyProfile }) {
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [canceledPeriodEnd, setCanceledPeriodEnd] = useState(null);

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const handleCancelSubscription = async () => {
    // We don't know the exact period-end date until Stripe tells us (no
    // subscription id is stored locally — see cancel-subscription's own
    // lookup), so the confirmation is intentionally generic; the precise
    // date is shown in the success message once we have it.
    const confirmed = window.confirm(
      "Your subscription will remain active until the end of your current billing period, then your account will move to the Free plan. Continue?"
    );
    if (!confirmed) return;

    setCanceling(true);
    setCancelError("");
    try {
      const { supabase: sb } = await import("../../supabaseClient");
      const { data, error } = await sb.functions.invoke("cancel-subscription");
      if (error || data?.error) {
        setCancelError(data?.error || error?.message || "Something went wrong");
        return;
      }
      setCanceledPeriodEnd(data.periodEnd);
      alert(`Your subscription is set to cancel on ${fmtDate(data.periodEnd)}. You'll keep full access until then.`);
    } catch (err) {
      setCancelError(String(err));
    } finally {
      setCanceling(false);
    }
  };

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16, marginBottom: 10 }}>
            💳 Plan &amp; Billing
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ background: planColor + "18", color: planColor, border: `1px solid ${planColor}33`, padding: "3px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: "'Manrope', sans-serif" }}>
              {planLabel}
            </span>
            {isPaid && !isOwner && companyProfile?.subscription_status && !canceledPeriodEnd && (
              <span style={{ fontSize: 12, color: "#9B9690", fontFamily: "'Manrope', sans-serif" }}>
                · {companyProfile.subscription_status}
              </span>
            )}
            {canceledPeriodEnd && (
              <span style={{ fontSize: 12, color: "#B08D57", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}>
                · Cancels {fmtDate(canceledPeriodEnd)}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: "#6b7c6b", lineHeight: 1.6, fontFamily: "'Manrope', sans-serif", maxWidth: 440 }}>
            {planDesc}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
          {!isPaid && (
            <a href="/pricing" style={{ display: "inline-block", padding: "9px 22px", background: "#163C2F", color: "#F5F1EA", borderRadius: 999, textDecoration: "none", fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "center" }}>
              Upgrade Plan ✦
            </a>
          )}
          {isPaid && !isOwner && (
            <>
              <a href="/pricing" style={{ display: "inline-block", padding: "9px 22px", background: "transparent", color: "#163C2F", border: "1.5px solid #163C2F", borderRadius: 999, textDecoration: "none", fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700, textAlign: "center" }}>
                Change Plan
              </a>
              <button
                onClick={() => alert("Stripe customer portal coming soon — contact support@leaslypro.com to manage your billing.")}
                style={{ padding: "9px 22px", background: "transparent", color: "#9B9690", border: "1px solid #D4CFC7", borderRadius: 999, fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
              >
                Manage Billing
              </button>
              {!canceledPeriodEnd && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={canceling}
                  style={{ padding: "9px 22px", background: "transparent", color: "#b91c1c", border: "1px solid #F5C6C6", borderRadius: 999, fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 600, cursor: canceling ? "default" : "pointer", opacity: canceling ? 0.6 : 1 }}
                >
                  {canceling ? "Canceling…" : "Cancel Subscription"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {cancelError && (
        <div style={{ marginTop: 14, padding: "10px 14px", background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, fontSize: 12, color: "#dc2626", fontFamily: "'Manrope', sans-serif", lineHeight: 1.6 }}>
          {cancelError}
        </div>
      )}
      {!isPaid && !isOwner && (
        <div style={{ marginTop: 14, padding: "10px 14px", background: "#FFFBF0", borderRadius: 8, border: "1px solid #B08D5733", fontSize: 12, color: "#92400E", fontFamily: "'Manrope', sans-serif", lineHeight: 1.6 }}>
          ✦ <strong>Core ($12/mo)</strong> unlocks email notifications, full maintenance tracking, document center, and payment history. <strong>Pro ($29/mo)</strong> adds team member access and advanced reports. Both plans include a <strong>14-day free trial</strong>.
        </div>
      )}
    </Card>
  );
}
